// ──────────────────────────────────────────────────────────────────────
// Wellbeing prompts — design-system widget matching the Upcoming Gigs
// shell: Card → WidgetHeader → Band of prompt rows. Each row is an
// innerBg surface with an icon leading slot, listPrimary title,
// listSecondary message, and a trailing dismiss button. The "checkin"
// prompt is row-clickable and opens the daily check-in dialog.
// ──────────────────────────────────────────────────────────────────────

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Droplet, Heart, Utensils, Moon, Activity, Clipboard, X, Eye, RefreshCw,
} from "lucide-react";
import { apiClient } from "@/App";
import { toast } from "sonner";
import { DragHandle } from "@/hooks/useDragHandle";
import { useIsDesktop } from "@/hooks/useIsDesktop";
import { Card, Band, WidgetHeader, GhostBtn } from "@/design-system/primitives";
import { tokens } from "@/design-system/tokens";

const { color, font, type, space, radius, border, motion } = tokens;

const ICON_MAP = {
  droplet: Droplet,
  heart: Heart,
  utensils: Utensils,
  moon: Moon,
  activity: Activity,
  clipboard: Clipboard,
};

// ───── Hide button (matches the other DS widgets) ───────────────────
function HideButton({ onHide }) {
  if (!onHide) return null;
  return (
    <button
      type="button"
      onClick={onHide}
      aria-label="Hide widget"
      title="Hide widget"
      style={{
        width: 32,
        height: 32,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        color: color.textMuted,
        border: "none",
        borderRadius: radius.md,
        cursor: "pointer",
      }}
    >
      <Eye className="h-4 w-4" />
    </button>
  );
}

// ───── Refresh button — clears local dismissals + refetches prompts.
// Frontend-only: server still remembers the dismissals, so on next page
// load the prompts will return to their dismissed state. This is by
// design — user wants to see everything *now*, not permanently un-dismiss.
function RefreshButton({ onRefresh, spinning }) {
  return (
    <button
      type="button"
      onClick={onRefresh}
      aria-label="Refresh prompts"
      title="Refresh prompts"
      data-testid="wellbeing-refresh"
      disabled={spinning}
      style={{
        width: 32,
        height: 32,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        background: "transparent",
        color: color.textMuted,
        border: "none",
        borderRadius: radius.md,
        cursor: spinning ? "default" : "pointer",
      }}
    >
      <RefreshCw className={`h-4 w-4 ${spinning ? "animate-spin" : ""}`} />
    </button>
  );
}

// ───── Scale selector (inside the daily check-in dialog) ─────────────
function ScaleSelector({ label, value, onChange, lowLabel, highLabel }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: space.xs }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontFamily: font.body,
          fontSize: 12,
          color: color.textMuted,
        }}
      >
        <span>{label}</span>
        <span>{value}/5</span>
      </div>
      <div style={{ display: "flex", gap: space.xs }}>
        {[1, 2, 3, 4, 5].map((n) => {
          const on = n <= value;
          return (
            <button
              key={n}
              onClick={() => onChange(n)}
              style={{
                flex: 1,
                height: 32,
                borderRadius: radius.md,
                fontFamily: font.body,
                fontSize: 12,
                fontWeight: 500,
                transition: `background-color ${motion.fast}`,
                backgroundColor: on ? color.orange : color.innerBg,
                color: on ? color.onOrange : color.textMuted,
                border: "none",
                cursor: "pointer",
              }}
              onMouseEnter={(e) => {
                if (!on) e.currentTarget.style.backgroundColor = color.innerBorder;
              }}
              onMouseLeave={(e) => {
                if (!on) e.currentTarget.style.backgroundColor = color.innerBg;
              }}
            >
              {n}
            </button>
          );
        })}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontFamily: font.body,
          fontSize: 10,
          color: color.textMuted,
        }}
      >
        <span>{lowLabel}</span>
        <span>{highLabel}</span>
      </div>
    </div>
  );
}

// ───── Prompt row ────────────────────────────────────────────────────
// Mirrors the ListRow visual hierarchy (innerBg surface, listPrimary +
// listSecondary typography) but allows multi-line secondary so prompt
// messages aren't truncated. Icon chip lives in the leading slot.
function PromptRow({ prompt, onClick, onDismiss }) {
  const Icon = ICON_MAP[prompt.icon] || Activity;
  const interactive = !!onClick;
  return (
    <div
      onClick={onClick}
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick(e);
              }
            }
          : undefined
      }
      data-testid={`wellbeing-${prompt.type}`}
      style={{
        display: "flex",
        alignItems: "center",
        gap: space.md,
        padding: `${space.md}px ${space.lg}px`,
        background: color.innerBg,
        border: border.inner,
        borderRadius: radius.lg,
        cursor: interactive ? "pointer" : "default",
        outline: "none",
        transition: `background ${motion.fast}`,
      }}
    >
      {/* Leading: bare icon at brand orange — quiet pictogram, no chip */}
      <div
        style={{
          flexShrink: 0,
          width: 24,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Icon style={{ width: 18, height: 18, color: color.orange }} />
      </div>

      {/* Body: title + multi-line message */}
      <div style={{ flex: "1 1 0", minWidth: 0 }}>
        <div
          style={{
            ...type.titleSm,
            color: color.text,
          }}
        >
          {prompt.title}
        </div>
        {prompt.message && (
          <div
            style={{
              ...type.bodySm,
              color: color.textDim,
              marginTop: 2,
              whiteSpace: "normal",
            }}
          >
            {prompt.message}
          </div>
        )}
      </div>

      {/* Trailing: dismiss */}
      <div
        style={{ display: "flex", alignItems: "center", flexShrink: 0 }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          title="Dismiss"
          data-testid={`dismiss-${prompt.type}`}
          style={{
            width: 32,
            height: 32,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "transparent",
            color: color.textMuted,
            border: "none",
            borderRadius: radius.md,
            cursor: "pointer",
            transition: `color ${motion.fast}`,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = color.text)}
          onMouseLeave={(e) => (e.currentTarget.style.color = color.textMuted)}
        >
          <X style={{ width: 14, height: 14 }} />
        </button>
      </div>
    </div>
  );
}

// ───── Empty state ──────────────────────────────────────────────────
function EmptyState() {
  return (
    <Band last padding={`${space.lg}px ${space.xl}px ${space.xl}px`}>
      <p
        style={{
          ...type.bodySm,
          color: color.textMuted,
          margin: 0,
        }}
      >
        All caught up
      </p>
    </Band>
  );
}

// ───── Public component ─────────────────────────────────────────────
export const WellbeingPrompts = ({ onHide } = {}) => {
  const [prompts, setPrompts] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());
  const [showCheckin, setShowCheckin] = useState(false);
  const [checkin, setCheckin] = useState({ energy: 3, pain: 1, mood: 3, notes: "" });
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  // Clear locally-dismissed prompts and refetch the source list. Server
  // dismissals are not touched, so this is a session-scoped "show me
  // everything again" — matches the dirty-tree status quo.
  const handleRefresh = async () => {
    if (refreshing) return;
    setRefreshing(true);
    setDismissed(new Set());
    try {
      await fetchPrompts();
    } finally {
      // Let the spin animation play through even if the fetch is instant.
      setTimeout(() => setRefreshing(false), 700);
    }
  };

  useEffect(() => {
    fetchPrompts();
    fetchDismissedPrompts();
  }, []);

  const fetchPrompts = async () => {
    try {
      const res = await apiClient.get("/wellbeing/prompts");
      setPrompts(res.data.prompts || []);
    } catch {}
  };

  const fetchDismissedPrompts = async () => {
    try {
      const res = await apiClient.get("/wellbeing/prompts/dismissed");
      if (res.data?.dismissed_types) {
        setDismissed(new Set(res.data.dismissed_types));
      }
    } catch {}
  };

  const handleDismiss = async (e, t) => {
    if (e && e.stopPropagation) e.stopPropagation();
    setDismissed((prev) => new Set([...prev, t]));
    try {
      await apiClient.post(`/wellbeing/prompts/${t}/dismiss`);
    } catch {
      setDismissed((prev) => {
        const next = new Set(prev);
        next.delete(t);
        return next;
      });
      toast.error("Failed to save dismissal");
    }
  };

  const handlePromptClick = (t) => {
    if (t === "checkin") setShowCheckin(true);
  };

  const handleCheckin = async () => {
    setSaving(true);
    try {
      await apiClient.post("/wellbeing/checkin", checkin);
      toast.success("Check-in saved!");
      setShowCheckin(false);
      await handleDismiss(null, "checkin");
    } catch {
      toast.error("Failed to save check-in");
    } finally {
      setSaving(false);
    }
  };

  const visiblePrompts = prompts.filter((p) => !dismissed.has(p.type));
  const isEmpty = visiblePrompts.length === 0;

  // Match the other dashboard widgets: tighter header depth on mobile so all
  // WidgetHeaders sit at the same height (Next Gig Brief is the exception —
  // it uses its own custom header, not WidgetHeader).
  const isDesktop = useIsDesktop(900);
  const compact = !isDesktop;
  const headerPadding = compact ? `${space.md}px ${space.md}px` : undefined;

  return (
    <>
      <Card
        maxWidth="none"
        style={{ height: "100%" }}
        data-testid="wellbeing-prompts-card"
      >
        <WidgetHeader
          title="Wellbeing"
          leading={<DragHandle />}
          padding={headerPadding}
          actions={
            <div style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
              <RefreshButton onRefresh={handleRefresh} spinning={refreshing} />
              <HideButton onHide={onHide} />
            </div>
          }
        />
        {isEmpty ? (
          <EmptyState />
        ) : (
          <Band last padding={`${space.md}px ${space.xl}px ${space.xl}px`}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: space.sm,
              }}
              data-testid="wellbeing-prompts"
            >
              {visiblePrompts.map((prompt) => (
                <PromptRow
                  key={prompt.type}
                  prompt={prompt}
                  onClick={
                    prompt.type === "checkin"
                      ? () => handlePromptClick(prompt.type)
                      : undefined
                  }
                  onDismiss={(e) => handleDismiss(e, prompt.type)}
                />
              ))}
            </div>
          </Band>
        )}
      </Card>

      <Dialog open={showCheckin} onOpenChange={setShowCheckin}>
        <DialogContent style={{ maxWidth: 448 }}>
          <DialogHeader>
            <DialogTitle>Daily Check-in</DialogTitle>
          </DialogHeader>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: space.xxl,
              paddingTop: space.md,
              paddingBottom: space.md,
            }}
          >
            <ScaleSelector
              label="Energy"
              value={checkin.energy}
              onChange={(v) => setCheckin((p) => ({ ...p, energy: v }))}
              lowLabel="Exhausted"
              highLabel="Buzzing"
            />
            <ScaleSelector
              label="Pain / Discomfort"
              value={checkin.pain}
              onChange={(v) => setCheckin((p) => ({ ...p, pain: v }))}
              lowLabel="None"
              highLabel="Severe"
            />
            <ScaleSelector
              label="Mood"
              value={checkin.mood}
              onChange={(v) => setCheckin((p) => ({ ...p, mood: v }))}
              lowLabel="Low"
              highLabel="Great"
            />
            <GhostBtn
              primary
              label={saving ? "Saving..." : "Save Check-in"}
              onClick={handleCheckin}
              disabled={saving}
              style={{
                width: "100%",
                justifyContent: "center",
                opacity: saving ? 0.7 : 1,
                cursor: saving ? "not-allowed" : "pointer",
              }}
              data-testid="checkin-submit"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default WellbeingPrompts;
